// Customer journey tracking — GA4 pattern
// Tracks journey stages: discovery → consideration → intent → purchase → loyalty
import { useCallback, useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";

type JourneyStage = "discovery" | "consideration" | "intent" | "purchase" | "loyalty";

const STORAGE_KEY = "djayma-journey";

interface JourneyState {
  readonly stage: JourneyStage;
  readonly entered_at: string;
  readonly total_visits: number;
  readonly products_viewed: number;
  readonly products_carted: number;
  readonly purchases: number;
}

function loadJourney(): JourneyState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as JourneyState;
  } catch { /* fresh start */ }
  return {
    stage: "discovery",
    entered_at: new Date().toISOString(),
    total_visits: 0,
    products_viewed: 0,
    products_carted: 0,
    purchases: 0,
  };
}

function saveJourney(state: JourneyState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* storage full, ignore */ }
}

function detectStage(state: JourneyState): JourneyStage {
  if (state.purchases > 2) return "loyalty";
  if (state.purchases > 0) return "purchase";
  if (state.products_carted > 0) return "intent";
  if (state.products_viewed > 2) return "consideration";
  return "discovery";
}

export function useCustomerJourney() {
  const stateRef = useRef(loadJourney());

  useEffect(() => {
    const updated = { ...stateRef.current, total_visits: stateRef.current.total_visits + 1 };
    stateRef.current = updated;
    saveJourney(updated);
  }, []);

  const recordAction = useCallback((action: "view" | "cart" | "purchase") => {
    const prev = stateRef.current;
    const updated = {
      ...prev,
      products_viewed: prev.products_viewed + (action === "view" ? 1 : 0),
      products_carted: prev.products_carted + (action === "cart" ? 1 : 0),
      purchases: prev.purchases + (action === "purchase" ? 1 : 0),
    };

    const newStage = detectStage(updated);
    if (newStage !== prev.stage) {
      trackEvent("journey_stage_changed", {
        from_stage: prev.stage,
        to_stage: newStage,
        time_in_previous_ms: Date.now() - new Date(prev.entered_at).getTime(),
        total_visits: updated.total_visits,
      });
      updated.stage = newStage;
      updated.entered_at = new Date().toISOString();
    }

    stateRef.current = updated;
    saveJourney(updated);
  }, []);

  return {
    stage: stateRef.current.stage,
    recordAction,
    journeyState: stateRef.current,
  };
}
